'use client'

import { useRef, useEffect } from 'react'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

import Button from '/react4xp/components/prototype/Button'

import type { HeaderMenuProps } from './types'

export default function HeaderMenu({ id, isOpen, columns = [], labelledBy, onClose, minHeight }: HeaderMenuProps) {
	const rootRef = useRef<HTMLDivElement>(null)

	// Content stays rendered while closed so Header can measure it; a fade is all open/close needs.
	useGSAP(
		() => {
			const el = rootRef.current
			if (!el) return
			gsap.killTweensOf(el)

			if (isOpen) {
				gsap.fromTo(el, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'power2.inOut' })
			} else {
				gsap.to(el, { autoAlpha: 0, duration: 0.3, ease: 'power2.inOut' })
			}
		},
		{ dependencies: [isOpen] },
	)

	useEffect(() => {
		function handleKeyDown(event: KeyboardEvent) {
			if (event.key === 'Escape' && isOpen) onClose()
		}
		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [isOpen, onClose])

	const columnCount = columns.length || 1
	const hasPromo = columns.some((column) => column.type === 'promo')
	const gridColsClass = hasPromo
		? 'lg:grid-cols-[7fr_7fr_7fr_10fr]'
		: columnCount === 4
			? 'lg:grid-cols-4'
			: columnCount === 3
				? 'lg:grid-cols-3'
				: columnCount === 2
					? 'lg:grid-cols-2'
					: 'lg:grid-cols-1'

	return (
		<div
			ref={rootRef}
			id={id}
			aria-labelledby={labelledBy}
			aria-hidden={!isOpen}
			{...(!isOpen ? ({ inert: '' } as Record<string, string>) : {})}
			style={minHeight ? { minHeight: `min(${minHeight}px, calc(100dvh - 5.75rem))` } : undefined}
			className={`fixed inset-x-0 top-23 z-40 max-h-[calc(100dvh-5.75rem)] overflow-y-auto bg-black-950 shadow-[0_2rem_4rem_-0.5rem_rgba(0,0,0,0.4)] ${
				isOpen ? 'visible opacity-100' : 'invisible opacity-0'
			}`}
		>
			<div data-header-menu-content className="container py-8">
				<div className={`grid grid-cols-1 divide-brand-900 lg:-mx-4 lg:divide-x ${gridColsClass}`}>
					{columns.map((column, index) =>
						column.type === 'links' ? (
							<div key={index} className="flex flex-col gap-6 lg:px-4">
								<a
									href={column.mainLink.url}
									className="flex items-start justify-between gap-2 border-b border-transparent pb-1 text-[1.375rem] leading-[1.4] font-medium text-white transition-colors duration-200 hover:border-brand-500 focus-visible:border-brand-500"
								>
									{column.mainLink.label}
									{column.mainLink.isExternal ? (
										<ArrowUpRight className="h-[1lh] w-5 shrink-0 text-brand-500" aria-hidden="true" />
									) : (
										<ArrowRight className="h-[1lh] w-5 shrink-0 text-brand-500" aria-hidden="true" />
									)}
								</a>
								{column.links.length > 0 && (
									<ul className="flex flex-col gap-2">
										{column.links.map((link, linkIndex) => (
											<li key={linkIndex} className="flex">
												<a
													href={link.url}
													className="flex w-full items-start justify-between gap-2 border-b border-transparent py-2.5 text-base leading-normal font-normal text-white transition-colors duration-200 hover:border-brand-500 focus-visible:border-brand-500"
												>
													{link.label}
													{link.isExternal ? (
														<ArrowUpRight className="h-[1lh] w-5 shrink-0 text-brand-500" aria-hidden="true" />
													) : (
														<ArrowRight className="h-[1lh] w-5 shrink-0 text-brand-500" aria-hidden="true" />
													)}
												</a>
											</li>
										))}
									</ul>
								)}
							</div>
						) : (
							<div key={index} className="lg:px-4">
								<div className="flex flex-col items-start gap-6 bg-brand-950 p-4 xl:p-8">
									<p className="text-[1.375rem] leading-[1.2] font-semibold text-white">{column.heading}</p>
									<p className="text-base leading-normal font-normal text-white">{column.description}</p>
									{column.ctaUrl && (
										<Button variant="primary" size="sm" href={column.ctaUrl}>
											{column.ctaLabel}
											{column.ctaIsExternal ? (
												<ArrowUpRight className="size-4" aria-hidden="true" />
											) : (
												<ArrowRight className="size-4" aria-hidden="true" />
											)}
										</Button>
									)}
								</div>
							</div>
						),
					)}
				</div>
			</div>
		</div>
	)
}
